import React, { FC, useCallback, useMemo, useState } from 'react';

import { Box, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import ExclamationMarkIcon from '../../../../assets/icons/ExclamationMarkIcon';
import { Loader } from '../../../../components/Loader';
import { CustomButton } from '../../../../components/UI/CustomButton';
import useScriptLoader, { SCRIPTS } from '../../../../context/ScriptLoader/useScriptLoader';
import { USER_ROLES } from '../../../../types';
import { useAuth, usePaidSockets, useUpdateUserInfoAndToken } from '../../../../utils/hooks';
import useStyles from './styles';

declare global {
  interface Window {
    Chargebee: any;
  }
}

interface NoSubscriptionViewProps {
  role?: string | null;
  ownedSubscription?: boolean;
  chargeBeeSite?: string;
  userId?: string | null;
}

const NoSubscriptionView: FC<NoSubscriptionViewProps> = ({
  chargeBeeSite,
  ownedSubscription,
  role,
  userId,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { getAccessTokenSilently } = useAuth();
  const [loading, setLoading] = useState(false);
  const isChargebeeLoaded = useScriptLoader(SCRIPTS.CHARGEBEE);

  const { updateUserInfo } = useUpdateUserInfoAndToken(() => setLoading(false));

  const renewCallback = useCallback(() => {
    updateUserInfo();
  }, [updateUserInfo]);

  usePaidSockets(renewCallback, userId);

  const cbInstance = useMemo(() => {
    if (!isChargebeeLoaded) return;
    return window?.Chargebee?.init({
      site: chargeBeeSite,
    });
  }, [chargeBeeSite, isChargebeeLoaded]);

  const hasControls =
    role === USER_ROLES.CompanyOwner ||
    role === USER_ROLES.B2CLearner ||
    role === USER_ROLES.LearnerWithoutSubscription;

  const openSelfServePortal = useCallback(async () => {
    try {
      cbInstance.setPortalSession(async () => {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_HTTP_API_URL}/api/chargebee/checkout-renew-form`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'client-referer': window?.location?.hostname,
            },
          },
        );
        const res = await response.text();
        return JSON.parse(res);
      });

      let cbPortal = cbInstance.createChargebeePortal();

      cbPortal.open({
        subscriptionResumed: () => {
          setLoading(true);
        },
        subscriptionReactivated: () => {
          setLoading(true);
        },
      });
    } catch (e) {}
  }, [getAccessTokenSilently, cbInstance]);

  if (loading) {
    return <Loader />;
  }

  return (
    <Box className={classes.container}>
      <ExclamationMarkIcon className={classes.icon} />
      <Typography className={classes.title}>{t('page.account.noSubscription')}</Typography>
      {hasControls ? (
        <Box className={classes.controls}>
          {ownedSubscription ? (
            <CustomButton onClick={openSelfServePortal}>
              {t('button.renewSubscription')}
            </CustomButton>
          ) : (
            <Link to="/pricing">
              <CustomButton>{t('button.manageMySubscription')}</CustomButton>
            </Link>
          )}
          <Link to="/contact" className={classes.link}>
            {t('button.contactSupport')}
          </Link>
        </Box>
      ) : (
        <Typography className={classes.description}>
          {role === USER_ROLES.TeamManager
            ? t('page.account.contactOwner')
            : t('page.account.contactManager')}
        </Typography>
      )}
    </Box>
  );
};

export default NoSubscriptionView;
